import { Block, Flex, Icon, IconProps, Typography } from '@aloft/toolkit-sui';
import { Image, chakra } from '@aloft/toolkit-sui/chakra';

import { useEffect, useState } from 'react';

import { Fallback } from './Fallback.tsx';

type ImageCarouselProps = {
  imageUrls: string[];
  selectedImageIx: number;
  selectPreviousImage: () => void;
  selectNextImage: () => void;
  nextDisabled?: boolean;
  previousDisabled?: boolean;
  fallbackURL?: string;
  fallbackText?: string;
  arrowSize?: IconProps['size'];
};

export const ImageCarousel = ({
  imageUrls,
  selectedImageIx,
  selectPreviousImage,
  selectNextImage,
  nextDisabled = false,
  previousDisabled = false,
  fallbackURL,
  fallbackText,
  arrowSize = 'md',
}: ImageCarouselProps) => {
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    setShowFallback(false);
  }, [imageUrls]);

  return (
    <Block height="100%" width="100%" position="relative" overflow="hidden">
      {!previousDisabled && (
        <Flex
          alignItems="center"
          justifyContent="center"
          position="absolute"
          left="0"
          top="0"
          height="100%"
          zIndex="2"
          padding={0}
        >
          <chakra.button
            type="button"
            onClick={selectPreviousImage}
            height="100%"
            _hover={{
              backgroundColor: 'rgb(237 240 255 / 45%)',
            }}
          >
            <Icon variant="outlined" icon="arrow-left" size={arrowSize} />
          </chakra.button>
        </Flex>
      )}

      {!(!fallbackURL && showFallback) && (
        <Image
          src={imageUrls[selectedImageIx] ?? fallbackURL}
          alt="property view"
          height="100%"
          width="100%"
          objectFit="cover"
          background="strokeDefault"
          onError={(e) => {
            console.warn('image loading failed, rendering fallback if available', fallbackURL);
            if (fallbackURL) {
              e.currentTarget.src = fallbackURL;
            }
            setShowFallback(true);
          }}
        />
      )}

      {showFallback &&
        (!fallbackURL ? (
          <Fallback fallbackText={fallbackText} />
        ) : (
          <Flex
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            width="100%"
            display="flex"
            justifyContent="center"
          >
            {fallbackText ?? (
              <Typography variant="body1" color="textOffset">
                &apos;Image not found :/&apos;
              </Typography>
            )}
          </Flex>
        ))}

      {!nextDisabled && (
        <Flex
          alignItems="center"
          justifyContent="center"
          position="absolute"
          right="0"
          top="0"
          height="100%"
          zIndex={2}
          padding={0}
        >
          <chakra.button
            type="button"
            onClick={selectNextImage}
            height="100%"
            _hover={{
              backgroundColor: 'rgb(237 240 255 / 45%)',
            }}
          >
            <Icon variant="outlined" icon="arrow-right" size={arrowSize} />
          </chakra.button>
        </Flex>
      )}
    </Block>
  );
};
